import { useState, useCallback, useRef, useEffect } from 'react';
import { getApiBaseUrl } from '../config/apiBase';
import { ensureStreamGateToken, invalidateStreamGateCache } from '../config/streamGate';
import { devWarn } from '../utils/devConsole';
import { markSearchTrace } from '../utils/searchTraceClient';
import {
  applyDocumentTitle,
  DEFAULT_WORKSPACE_TITLE,
  resolveScanDocumentTitle,
} from '../utils/scanTabTitle';
import { releaseScanWakeLock, requestScanWakeLock } from '../utils/scanWakeLock';

/** Stable first keys for person-scan SSE URLs (matches backend expectations). */
const STREAM_PARAM_ORDER = [
  'name',
  'scope',
  'context_focus',
  'context_locations',
  'context_username',
];

const CSV_CONTEXT_KEYS = new Set(['context_focus', 'context_locations']);

/**
 * Trim, collapse whitespace, strip stray separators at ends (avoids "Romani ," vs "Romania").
 */
function sanitizeToken(s) {
  if (s === undefined || s === null) return '';
  let t = String(s)
    .replace(/\u200b/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^[,\s;]+|[,\s;]+$/g, '')
    .trim();
  if (typeof t.normalize === 'function') {
    t = t.normalize('NFC');
  }
  return t;
}

function parseCsv(val) {
  if (val === undefined || val === null) return [];
  return String(val)
    .split(',')
    .map(sanitizeToken)
    .filter(Boolean);
}

/** Cleans context strings before building stream query params. */
function sanitizeStreamParams(params) {
  if (!params || typeof params !== 'object') return {};

  const p = { ...params };
  const out = {};

  for (const [key, val] of Object.entries(p)) {
    if (val === undefined || val === null) continue;
    if (typeof val === 'string') {
      if (CSV_CONTEXT_KEYS.has(key)) {
        const parts = parseCsv(val);
        if (parts.length) out[key] = parts.join(',');
      } else {
        const t = sanitizeToken(val);
        if (t) out[key] = t;
      }
    } else {
      out[key] = val;
    }
  }

  return out;
}

/**
 * Build ordered query string via URLSearchParams (correct encoding for commas & special chars).
 */
function buildEncodedQuery(params) {
  const sanitized = sanitizeStreamParams(params);
  const keys = [];
  for (const k of STREAM_PARAM_ORDER) {
    if (Object.prototype.hasOwnProperty.call(sanitized, k)) keys.push(k);
  }
  const rest = Object.keys(sanitized)
    .filter((k) => !STREAM_PARAM_ORDER.includes(k))
    .sort();
  keys.push(...rest);

  const usp = new URLSearchParams();
  for (const k of keys) {
    const v = sanitized[k];
    if (v === undefined || v === null) continue;
    const str = typeof v === 'string' ? v : String(v);
    if (str === '') continue;
    usp.append(k, str);
  }
  return usp.toString();
}

/** Full absolute URL for an SSE stream (optional helper / tests). */
function buildStreamUrl(baseUrl, endpoint, params) {
  const q = buildEncodedQuery(params);
  const path = `${String(baseUrl).replace(/\/$/, '')}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  return q ? `${path}?${q}` : path;
}

/** Stable fingerprint for company SSE rows (dedupe when appending location refinement). */
function companyStreamFingerprint(r) {
  if (!r || r.type === 'log') return null;
  // Cache telemetry rows must not share a dedupe bucket with real intel (they often have no url/title).
  if (r.type === 'cache_meta') return null;
  const u = String(r.url || '')
    .trim()
    .toLowerCase();
  if (u) return `u:${r.ui_bucket || ''}:${u}`;
  if (r.ui_bucket === 'relationship_node' && r.target_entity) {
    return `rel:${String(r.target_entity).toLowerCase()}:${u || 'nourl'}`;
  }
  const t = String(r.title || '').slice(0, 160);
  const s = String(r.snippet || '').slice(0, 120);
  const typ = String(r.type || '');
  const src = String(r.source || '');
  const st = String(r.status || '');
  return `t:${r.ui_bucket || ''}:${typ}:${src}:${st}:${t}:${s}`;
}

function isTerminalEvent(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.done === true) return true;
  if (
    data.status === 'completed' &&
    !data.ui_bucket &&
    !data.source &&
    data.type !== 'log'
  ) {
    return true;
  }
  return data.status === 'cancelled';
}

const useOsintStream = (baseUrl = getApiBaseUrl()) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  const eventSourceRef = useRef(null);
  const sessionIdRef = useRef(null);
  const isStreamActive = useRef(false);
  const mergeFpRef = useRef(null);
  const appendLabelRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastEventIdRef = useRef(0);
  const wakeLockRef = useRef(null);
  const scanStartedAtRef = useRef(0);
  const resultsCountRef = useRef(0);
  const loadingRef = useRef(false);
  const completedRef = useRef(false);
  const reconcileBusyRef = useRef(false);
  const pollTimerRef = useRef(null);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const releaseWake = useCallback(async () => {
    const lock = wakeLockRef.current;
    wakeLockRef.current = null;
    await releaseScanWakeLock(lock);
  }, []);

  const acquireWake = useCallback(async () => {
    await releaseWake();
    wakeLockRef.current = await requestScanWakeLock();
  }, [releaseWake]);

  const updateTabTitle = useCallback(() => {
    if (typeof document === 'undefined') return;
    const elapsedSec = scanStartedAtRef.current
      ? Math.max(0, Math.floor((Date.now() - scanStartedAtRef.current) / 1000))
      : 0;
    const title = resolveScanDocumentTitle({
      hidden: Boolean(document.hidden),
      scanning: loadingRef.current && !completedRef.current,
      completed: completedRef.current,
      resultsCount: resultsCountRef.current,
      elapsedSec,
    });
    applyDocumentTitle(title);
  }, []);

  const ingestPayload = useCallback((data, { fromReplay = false } = {}) => {
    if (!data || typeof data !== 'object') return { terminal: false };

    if (data.session_id) {
      sessionIdRef.current = data.session_id;
      setSessionId(data.session_id);
      return { terminal: false };
    }

    if (isTerminalEvent(data)) {
      isStreamActive.current = false;
      mergeFpRef.current = null;
      appendLabelRef.current = null;
      abortControllerRef.current = null;
      loadingRef.current = false;
      completedRef.current = true;
      setCompletionData(data);
      setLoading(false);
      setCompleted(true);
      clearPollTimer();
      releaseWake();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      updateTabTitle();
      return { terminal: true };
    }

    if (data.type === 'log') {
      setResults((prev) => {
        const next = [...prev, data];
        resultsCountRef.current = next.filter((r) => r && r.type !== 'log').length;
        return next;
      });
      return { terminal: false };
    }

    const fp = companyStreamFingerprint(data);
    const fps = mergeFpRef.current;
    if (fps && fp && fps.has(fp)) {
      return { terminal: false };
    }
    if (fps && fp) fps.add(fp);

    const label = appendLabelRef.current;
    const tagged =
      label && data && typeof data === 'object'
        ? {
            ...data,
            wiw_result_origin: fromReplay ? 'resume_replay' : 'location_append',
            wiw_location_context: label,
          }
        : fromReplay && data && typeof data === 'object'
          ? { ...data, wiw_result_origin: 'resume_replay' }
          : data;

    setResults((prev) => {
      const next = [...prev, tagged];
      resultsCountRef.current = next.filter((r) => r && r.type !== 'log').length;
      return next;
    });
    return { terminal: false };
  }, [clearPollTimer, releaseWake, updateTabTitle]);

  const fetchMissedEvents = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return { status: 'unknown', applied: 0, terminal: false };
    const since = lastEventIdRef.current || 0;
    try {
      const res = await fetch(
        `${baseUrl}/scan/${encodeURIComponent(sid)}/events?since=${since}`,
        { credentials: 'same-origin' },
      );
      if (!res.ok) return { status: 'unknown', applied: 0, terminal: false };
      const data = await res.json();
      const rows = Array.isArray(data?.events) ? data.events : [];
      let terminal = false;
      let applied = 0;
      for (const row of rows) {
        const eid = Number(row?.id) || 0;
        if (eid > lastEventIdRef.current) lastEventIdRef.current = eid;
        const ev = row?.event;
        if (!ev || typeof ev !== 'object') continue;
        const outcome = ingestPayload(ev, { fromReplay: true });
        applied += 1;
        if (outcome.terminal) {
          terminal = true;
          break;
        }
      }
      return {
        status: String(data?.status || 'unknown'),
        applied,
        terminal,
        lastEventId: data?.last_event_id,
      };
    } catch {
      return { status: 'unknown', applied: 0, terminal: false };
    }
  }, [baseUrl, ingestPayload]);

  const startMissedEventPolling = useCallback(() => {
    clearPollTimer();
    pollTimerRef.current = window.setInterval(() => {
      if (!loadingRef.current || completedRef.current) {
        clearPollTimer();
        return;
      }
      fetchMissedEvents().then((out) => {
        if (out.terminal || out.status === 'completed' || out.status === 'cancelled') {
          clearPollTimer();
        }
      });
    }, 2500);
  }, [clearPollTimer, fetchMissedEvents]);

  const reconcileOnVisible = useCallback(async () => {
    if (reconcileBusyRef.current) return;
    if (!loadingRef.current || completedRef.current) {
      updateTabTitle();
      return;
    }
    reconcileBusyRef.current = true;
    try {
      const es = eventSourceRef.current;
      const esOpen = es && es.readyState === 1; // OPEN
      if (!esOpen && sessionIdRef.current) {
        const missed = await fetchMissedEvents();
        if (!missed.terminal && (missed.status === 'running' || missed.status === 'draining' || missed.status === 'unknown')) {
          startMissedEventPolling();
        }
      } else if (esOpen && sessionIdRef.current) {
        // Still open — pull any gap that may have been buffered server-side.
        await fetchMissedEvents();
      }
      // Re-assert wake lock after returning from background (browsers often drop it).
      if (loadingRef.current && !completedRef.current) {
        await acquireWake();
      }
    } finally {
      reconcileBusyRef.current = false;
      updateTabTitle();
    }
  }, [acquireWake, fetchMissedEvents, startMissedEventPolling, updateTabTitle]);

  /** Stops SSE immediately, clears hook state, notifies backend cancel (best-effort). */
  const interruptStream = useCallback(() => {
    const sid = sessionIdRef.current;
    isStreamActive.current = false;
    loadingRef.current = false;
    completedRef.current = false;
    clearPollTimer();
    releaseWake();
    applyDocumentTitle(DEFAULT_WORKSPACE_TITLE);
    try {
      abortControllerRef.current?.abort();
    } catch {
      /* ignore */
    }
    abortControllerRef.current = null;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    sessionIdRef.current = null;
    mergeFpRef.current = null;
    appendLabelRef.current = null;
    lastEventIdRef.current = 0;
    setResults([]);
    setLoading(false);
    setCompleted(false);
    setCompletionData(null);
    setSessionId(null);
    if (sid) {
      fetch(`${baseUrl}/cancel-stream-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid }),
      }).catch(() => {});
    }
  }, [baseUrl, clearPollTimer, releaseWake]);

  const startStream = useCallback(async (endpoint, params = {}, streamOptions = {}) => {
    if (isStreamActive.current) {
      devWarn('Stream already active, ignoring duplicate call.');
      return;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    try {
      abortControllerRef.current?.abort();
    } catch {
      /* ignore prior abort */
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isStreamActive.current = true;
    clearPollTimer();
    lastEventIdRef.current = 0;
    scanStartedAtRef.current = Date.now();
    resultsCountRef.current = 0;
    loadingRef.current = true;
    completedRef.current = false;

    const {
      mergeBaseResults = null,
      mergeBaseLogs = null,
      appendLocationsLabel = null,
    } = streamOptions || {};

    const baseIntel =
      Array.isArray(mergeBaseResults) && mergeBaseResults.length > 0
        ? mergeBaseResults.map((r) => ({ ...r }))
        : null;
    const baseLogs =
      Array.isArray(mergeBaseLogs) && mergeBaseLogs.length > 0
        ? mergeBaseLogs.map((l) => ({ ...l }))
        : null;

    const fpSet = new Set();
    if (baseIntel) {
      for (const row of baseIntel) {
        const fp = companyStreamFingerprint(row);
        if (fp) fpSet.add(fp);
      }
    }
    mergeFpRef.current = fpSet;
    appendLabelRef.current =
      typeof appendLocationsLabel === 'string' && appendLocationsLabel.trim()
        ? appendLocationsLabel.trim()
        : null;

    const initialRows = [];
    if (baseLogs) initialRows.push(...baseLogs);
    if (baseIntel) initialRows.push(...baseIntel);
    if (appendLabelRef.current && (baseIntel || baseLogs)) {
      const ts = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      initialRows.push({
        type: 'log',
        message: `WiW: keeping previous output — appending additional results (${appendLabelRef.current}).`,
        timestamp: ts,
      });
    }

    setResults(initialRows);
    resultsCountRef.current = initialRows.filter((r) => r && r.type !== 'log').length;
    setLoading(true);
    setError(null);
    setCompleted(false);
    setCompletionData(null);
    setSessionId(null);
    await acquireWake();
    updateTabTitle();

    let streamParams = { ...params };
    try {
      markSearchTrace('stream_gate_start');
      const gate = await ensureStreamGateToken(baseUrl);
      markSearchTrace('stream_gate_done', { detail: gate ? 'token ok' : 'gate disabled' });
      if (abortController.signal.aborted) {
        isStreamActive.current = false;
        loadingRef.current = false;
        setLoading(false);
        await releaseWake();
        return;
      }
      if (gate) {
        streamParams = { ...streamParams, stream_gate: gate };
      }
    } catch (e) {
      isStreamActive.current = false;
      loadingRef.current = false;
      setLoading(false);
      setError(String(e?.message || e || 'Could not start stream'));
      await releaseWake();
      return;
    }

    if (abortController.signal.aborted) {
      isStreamActive.current = false;
      loadingRef.current = false;
      setLoading(false);
      await releaseWake();
      return;
    }

    const url = buildStreamUrl(baseUrl, endpoint, streamParams);

    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;
    markSearchTrace('sse_open', { endpoint });

    let firstEventLogged = false;
    let localEventSeq = 0;

    const onAbort = () => {
      if (eventSourceRef.current === eventSource) {
        eventSource.close();
        eventSourceRef.current = null;
      }
    };
    abortController.signal.addEventListener('abort', onAbort, { once: true });

    eventSource.onmessage = (event) => {
      try {
        if (!firstEventLogged) {
          firstEventLogged = true;
          markSearchTrace('sse_first_event', { endpoint });
        }
        localEventSeq += 1;
        const headerId = Number(event.lastEventId);
        if (!Number.isNaN(headerId) && headerId > 0) {
          lastEventIdRef.current = Math.max(lastEventIdRef.current, headerId);
        } else if (localEventSeq > lastEventIdRef.current) {
          lastEventIdRef.current = localEventSeq;
        }
        const data = JSON.parse(event.data);
        ingestPayload(data);
        updateTabTitle();
      } catch (err) {
        console.error('Error parsing SSE message:', err);
      }
    };
    eventSource.onerror = () => {
      invalidateStreamGateCache();
      // Soft failure: keep scan active and reconcile via /scan/{id}/events when possible.
      if (eventSourceRef.current === eventSource) {
        try {
          eventSource.close();
        } catch {
          /* ignore */
        }
        eventSourceRef.current = null;
      }
      if (loadingRef.current && sessionIdRef.current && !completedRef.current) {
        startMissedEventPolling();
        updateTabTitle();
        return;
      }
      isStreamActive.current = false;
      mergeFpRef.current = null;
      appendLabelRef.current = null;
      loadingRef.current = false;
      setLoading(false);
      releaseWake();
      updateTabTitle();
    };
  }, [
    acquireWake,
    baseUrl,
    clearPollTimer,
    ingestPayload,
    releaseWake,
    startMissedEventPolling,
    updateTabTitle,
  ]);

  useEffect(() => {
    const onVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible') {
        reconcileOnVisible();
      } else {
        updateTabTitle();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    const titleTick = window.setInterval(() => {
      if (loadingRef.current && typeof document !== 'undefined' && document.hidden) {
        updateTabTitle();
      }
    }, 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(titleTick);
      clearPollTimer();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        isStreamActive.current = false;
      }
      releaseWake();
      applyDocumentTitle(DEFAULT_WORKSPACE_TITLE);
    };
  }, [clearPollTimer, reconcileOnVisible, releaseWake, updateTabTitle]);

  const cancelStream = useCallback(async () => {
    isStreamActive.current = false;
    loadingRef.current = false;
    completedRef.current = true;
    clearPollTimer();
    await releaseWake();
    applyDocumentTitle(DEFAULT_WORKSPACE_TITLE);

    try {
      abortControllerRef.current?.abort();
    } catch {
      /* ignore */
    }
    abortControllerRef.current = null;

    if (!sessionIdRef.current && !sessionId) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      sessionIdRef.current = null;
      mergeFpRef.current = null;
      appendLabelRef.current = null;
      setLoading(false);
      setCompleted(true);
      setCompletionData({ status: 'cancelled', done: true });
      return;
    }

    const sid = sessionIdRef.current || sessionId;
    try {
      await fetch(`${baseUrl}/cancel-stream-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid }),
      });
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      sessionIdRef.current = null;
      mergeFpRef.current = null;
      appendLabelRef.current = null;
      setLoading(false);
      setCompleted(true);
      setCompletionData({ status: 'cancelled', done: true });
    } catch (err) {
      console.error('Error cancelling stream:', err);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setLoading(false);
      setCompleted(true);
      setCompletionData({ status: 'cancelled', done: true });
    }
  }, [baseUrl, clearPollTimer, releaseWake, sessionId]);

  /** Alias for CANCEL UX — aborts AbortController + closes EventSource. */
  const abortScan = cancelStream;

  const resetStream = useCallback(() => {
    isStreamActive.current = false;
    loadingRef.current = false;
    completedRef.current = false;
    clearPollTimer();
    releaseWake();
    applyDocumentTitle(DEFAULT_WORKSPACE_TITLE);
    try {
      abortControllerRef.current?.abort();
    } catch {
      /* ignore */
    }
    abortControllerRef.current = null;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    sessionIdRef.current = null;
    mergeFpRef.current = null;
    appendLabelRef.current = null;
    lastEventIdRef.current = 0;
    setResults([]);
    setLoading(false);
    setError(null);
    setCompleted(false);
    setCompletionData(null);
    setSessionId(null);
  }, [clearPollTimer, releaseWake]);

  return {
    loading,
    error,
    results,
    completed,
    completionData,
    sessionId,
    startStream,
    cancelStream,
    abortScan,
    resetStream,
    interruptStream,
  };
};

export default useOsintStream;
export { buildEncodedQuery, buildStreamUrl, sanitizeStreamParams, companyStreamFingerprint };
